/**
 * MMER Game Prediction Engine — ported from MarchMetrix Phase 5E
 *
 * Uses adjusted efficiency margins (adjOE, adjDE), tempo, and four factors
 * to compute point spreads and win probabilities.
 */

// ── Types ────────────────────────────────────────────────────
export interface MMERTeamData {
  adjOE: number;
  adjDE: number;
  adjEM: number;
  tempo: number;
  mmerRank: number;
  gamesPlayed?: number;
  fourFactors?: {
    offense: { eFG: number; toPct: number; orbPct: number; ftRate: number };
    defense: { eFG: number; toPct: number; orbPct: number; ftRate: number };
  };
}

export interface MMERPrediction {
  expectedTempo: number;
  teamAScore: number;
  teamBScore: number;
  spread: number;           // negative = teamB favored
  winProbA: number;          // 0-1
  winProbB: number;          // 0-1
  matchupEdges: {
    shooting: number;
    turnovers: number;
    rebounding: number;
    freeThrows: number;
  } | null;
}

// ── Constants ────────────────────────────────────────────────
const LEAGUE_AVG_TEMPO = 67.5;
const LEAGUE_AVG_EFF = 100;

// Floor profile for teams not in MMER dataset (typical low-major / 16 seed)
const FLOOR_TEAM: MMERTeamData = {
  adjOE: 95,
  adjDE: 110,
  adjEM: -15,
  tempo: 66,
  mmerRank: 400,
  fourFactors: {
    offense: { eFG: 46, toPct: 20, orbPct: 27, ftRate: 28 },
    defense: { eFG: 52, toPct: 14, orbPct: 32, ftRate: 34 },
  },
};

// Common name aliases for teams with non-obvious slug mappings
const TEAM_ALIASES: Record<string, string> = {
  'liu': 'long-island-university',
  'miami-(fl)': 'miami-fl',
  'miami-fl': 'miami-fl',
  'texas-a&m': 'texas-am',
  'texas-a-m': 'texas-am',
  'prairie-view-a&m': 'prairie-view',
  'prairie-view-a-m': 'prairie-view',
  'penn': 'pennsylvania',
  'hawaii': 'hawaii-rainbow',
  'saint-mary\'s': 'saint-marys',
  'st.-john\'s': 'st-johns',
  'long-island': 'long-island-university',
  'uconn': 'connecticut',
  'smu': 'southern-methodist',
  'vcu': 'virginia-commonwealth',
  'ucf': 'central-florida',
  'lsu': 'louisiana-st',
  'ole-miss': 'mississippi',
  'pitt': 'pittsburgh',
  'usc': 'southern-california',
  'byu': 'brigham-young',
  'tcu': 'texas-christian',
  'unc': 'north-carolina',
  'umass': 'massachusetts',
  'unlv': 'nevada-las-vegas',
  'utep': 'texas-el-paso',
  'fau': 'florida-atlantic',
  'fiu': 'florida-international',
  'etsu': 'east-tennessee-st',
  'siue': 'siu-edwardsville',
  'uncw': 'unc-wilmington',
  'st-johns': 'st-johns',
  'michigan-st': 'michigan-st',
  'mississippi-st': 'mississippi-st',
  'san-diego-st': 'san-diego-st',
  'utah-st': 'utah-st',
  'morehead-st': 'morehead-st',
  'norfolk-st': 'norfolk-st',
  'nc-state': 'north-carolina-st',
  'grand-canyon': 'grand-canyon',
  'mich-st': 'michigan-st',
  'st-john': 'st-johns',
  'st-john-s': 'st-johns',
  'n-iowa': 'northern-iowa',
  'n--iowa': 'northern-iowa',
  'iowa-st-': 'iowa-st',
  'saint-mary-s': 'saint-marys',
  'usf': 'south-florida',
  'cal-baptist': 'california-baptist',
  'ndsu': 'north-dakota-st',
  'hawai': 'hawaii-rainbow',
  'vandy': 'vanderbilt',
  'st-mary': 'saint-marys',
  'tenn-st': 'tennessee-st',
};

// ── Singleton data cache ─────────────────────────────────────
let _effData: Record<string, MMERTeamData> | null = null;
let _slugMap: Record<string, string> | null = null;  // espnId → slug
let _loadPromise: Promise<void> | null = null;

export async function loadMMERData(): Promise<void> {
  if (_effData! && _slugMap) return;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    try {
      const [effResp, slugResp] = await Promise.all([
        fetch('/mmer-efficiency.json?v=2'),
        fetch('/mmer-slug-map.json?v=2'),
      ]);
      const effJson = await effResp.json();
      _effData! = effJson.teams || effJson;
      console.log('[MMER] Loaded efficiency data, keys:', Object.keys(_effData!).length, 'has 2567:', '2567' in _effData!, 'rank:', _effData!['2567']?.mmerRank);
      _slugMap = await slugResp.json();
    } catch (e) {
      console.warn('[MMER] Failed to load efficiency data:', e);
      _effData! = {};
      _slugMap = {};
    }
  })();

  return _loadPromise;
}

export function getMMERData(): Record<string, MMERTeamData> {
  return _effData! || {};
}

export function getSlugMap(): Record<string, string> {
  return _slugMap || {};
}

// ── Team resolution ──────────────────────────────────────────
/**
 * Resolve a team identifier (ESPN ID, slug, shortName) to MMER data.
 */
export function getFloorTeam(): MMERTeamData { return { ...FLOOR_TEAM }; }

// ── Precomputed rankings across all teams ──────────────────────────────────

// ── Resume data (quadrant records, road/neutral, top wins) ─────────────────
let _resumeData: Record<string, any> | null = null;
let _resumeLoading = false;

export async function loadResumeData(): Promise<void> {
  if (_resumeData || _resumeLoading) return;
  _resumeLoading = true;
  try {
    const res = await fetch('/mmer-resumes.json?v=2');
    _resumeData = await res.json();
  } catch { _resumeData = {}; }
  _resumeLoading = false;
}

export function getResumeData(espnId: string): any | null {
  return _resumeData?.[espnId] || null;
}

let _rankings: Record<string, Record<string, number>> | null = null;

export function getMMERRankings(): Record<string, Record<string, number>> {
  if (_rankings) return _rankings;
  const data = getMMERData();
  if (!data || Object.keys(data).length === 0) return {};

  const teams = Object.entries(data).filter(([_, t]) => (t as any).gamesPlayed >= 25).map(([id, t]) => ({ id, ...t }));
  
  // Sort each stat and assign ranks
  const stats: Array<{ key: string; field: (t: any) => number | undefined; lower?: boolean }> = [
    { key: 'adjOE', field: t => t.adjOE },
    { key: 'adjDE', field: t => t.adjDE, lower: true },
    { key: 'adjEM', field: t => t.adjEM },
    { key: 'tempo', field: t => t.tempo },
    { key: 'offEFG', field: t => t.fourFactors?.offense?.eFG },
    { key: 'offTO', field: t => t.fourFactors?.offense?.toPct, lower: true },
    { key: 'offORB', field: t => t.fourFactors?.offense?.orbPct },
    { key: 'offFT', field: t => t.fourFactors?.offense?.ftRate },
    { key: 'defEFG', field: t => t.fourFactors?.defense?.eFG, lower: true },
    { key: 'defTO', field: t => t.fourFactors?.defense?.toPct },
    { key: 'defORB', field: t => t.fourFactors?.defense?.orbPct, lower: true },
    { key: 'defFT', field: t => t.fourFactors?.defense?.ftRate, lower: true },
  ];

  const result: Record<string, Record<string, number>> = {};
  for (const { key, field, lower } of stats) {
    const sorted = teams
      .filter(t => field(t) != null)
      .sort((a, b) => lower ? (field(a)! - field(b)!) : (field(b)! - field(a)!));
    for (let i = 0; i < sorted.length; i++) {
      if (!result[sorted[i].id]) result[sorted[i].id] = {};
      result[sorted[i].id][key] = i + 1;
    }
  }
  _rankings = result;
  return result;
}

export function resolveMMERTeam(query: string): (MMERTeamData & { espnId: string }) | null {
  const data = getMMERData();
  const slugMap = getSlugMap();

  // Direct ESPN ID match
  if (data[query]) {
    return { espnId: query, ...data[query] };
  }

  // Reverse slug map: slug → espnId
  const slugToEspn: Record<string, string> = {};
  for (const [espnId, slug] of Object.entries(slugMap)) {
    slugToEspn[slug] = espnId;
  }

  const rawNormalized = query.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const normalized = TEAM_ALIASES[rawNormalized] || rawNormalized;

  // Exact slug match
  if (slugToEspn[normalized] && data[slugToEspn[normalized]]) {
    console.log('[MMER] Exact slug match:', normalized, '→ ESPN', slugToEspn[normalized], 'rank', data[slugToEspn[normalized]].mmerRank);
    return { espnId: slugToEspn[normalized], ...data[slugToEspn[normalized]] };
  }
  console.log('[MMER] No exact slug match for:', normalized, 'slugToEspn has it:', normalized in slugToEspn, 'data has espn:', slugToEspn[normalized], data[slugToEspn[normalized]]?.mmerRank);

  // Fuzzy slug match
  const candidates: { slug: string; espnId: string; exactness: number }[] = [];
  for (const [slug, espnId] of Object.entries(slugToEspn)) {
    if (slug === normalized || slug.includes(normalized) || normalized.includes(slug)) {
      if (data[espnId]) {
        candidates.push({ slug, espnId, exactness: slug === normalized ? 0 : Math.abs(slug.length - normalized.length) });
      }
    }
  }
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.exactness - b.exactness);
    const best = candidates[0];
    return { espnId: best.espnId, ...data[best.espnId] };
  }

  return null;
}

// ── Prediction engine ────────────────────────────────────────
export function predictGame(
  teamA: MMERTeamData,
  teamB: MMERTeamData,
  _neutral = true,
): MMERPrediction {
  // Expected tempo: geometric mean of both teams' tempos relative to league average
  const expectedTempo = (teamA.tempo * teamB.tempo) / LEAGUE_AVG_TEMPO;

  // Expected points per 100 possessions for each team
  const teamAOffEff = teamA.adjOE + (teamB.adjDE - LEAGUE_AVG_EFF);
  const teamBOffEff = teamB.adjOE + (teamA.adjDE - LEAGUE_AVG_EFF);

  // Scale to actual game possessions
  const teamAScore = (teamAOffEff / 100) * expectedTempo;
  const teamBScore = (teamBOffEff / 100) * expectedTempo;

  // Spread = teamA score - teamB score
  const spread = teamAScore - teamBScore;

  // Win probability using logistic function
  // ~1 point of spread ≈ 3% win probability shift near 50%
  // Logistic divisor: 11 balances signal vs overconfidence
  // (lower = steeper curve = more confident; higher = flatter = more conservative)
  const rawWinProbA = 1 / (1 + Math.pow(10, -spread / 11));

  // Clamp to 1-99% for display credibility
  const winProbA = Math.max(0.01, Math.min(0.99, rawWinProbA));
  const winProbB = 1 - winProbA;

  // Four factors matchup edges
  let matchupEdges = null;
  if (teamA.fourFactors && teamB.fourFactors) {
    matchupEdges = {
      shooting: Math.round(((teamA.fourFactors.offense.eFG - teamB.fourFactors.defense.eFG) -
                (teamB.fourFactors.offense.eFG - teamA.fourFactors.defense.eFG)) * 10) / 10,
      turnovers: Math.round(((teamB.fourFactors.offense.toPct - teamA.fourFactors.defense.toPct) -
                 (teamA.fourFactors.offense.toPct - teamB.fourFactors.defense.toPct)) * 10) / 10,
      rebounding: Math.round(((teamA.fourFactors.offense.orbPct - teamB.fourFactors.defense.orbPct) -
                  (teamB.fourFactors.offense.orbPct - teamA.fourFactors.defense.orbPct)) * 10) / 10,
      freeThrows: Math.round(((teamA.fourFactors.offense.ftRate - teamB.fourFactors.defense.ftRate) -
                  (teamB.fourFactors.offense.ftRate - teamA.fourFactors.defense.ftRate)) * 10) / 10,
    };
  }

  return {
    expectedTempo: Math.round(expectedTempo * 10) / 10,
    teamAScore: Math.round(teamAScore * 10) / 10,
    teamBScore: Math.round(teamBScore * 10) / 10,
    spread: Math.round(spread * 10) / 10,
    winProbA,
    winProbB,
    matchupEdges,
  };
}

/**
 * Quick-predict: resolve two team names/IDs and return prediction.
 * Returns null if either team can't be resolved.
 */
export function quickPredict(teamAQuery: string, teamBQuery: string): (MMERPrediction & { teamAName: string; teamBName: string }) | null {
  const a = resolveMMERTeam(teamAQuery);
  const b = resolveMMERTeam(teamBQuery);
  if (!a || !b) return null;

  const prediction = predictGame(a, b, true);
  const slugMap = getSlugMap();

  return {
    ...prediction,
    teamAName: slugMap[a.espnId] || teamAQuery,
    teamBName: slugMap[b.espnId] || teamBQuery,
  };
}

// ── Historical Seed Prior Calibration ──────────────────────────
// Historical win rates for higher seed in NCAA tournament matchups (1985-2025)
// Source: aggregated from 40 years of tournament data
const SEED_MATCHUP_PRIORS: Record<string, number> = {
  '1v16': 0.99, '1v8': 0.80, '1v9': 0.85, '1v5': 0.65, '1v12': 0.75,
  '1v4': 0.60, '1v13': 0.70,
  '2v15': 0.94, '2v7': 0.60, '2v10': 0.65, '2v3': 0.55,
  '3v14': 0.85, '3v6': 0.55, '3v11': 0.65,
  '4v13': 0.80, '4v5': 0.55, '4v12': 0.65,
  '5v12': 0.65, '5v4': 0.45,
  '6v11': 0.63, '6v3': 0.45,
  '7v10': 0.61, '7v2': 0.40,
  '8v9': 0.52, '8v1': 0.20,
  '9v8': 0.48, '10v7': 0.39,
  '11v6': 0.37, '12v5': 0.35,
  '13v4': 0.20, '14v3': 0.15,
  '15v2': 0.06, '16v1': 0.01,
};

/**
 * Look up historical win rate for seedA vs seedB.
 * Returns null if no prior exists for this matchup.
 */
function getSeedPrior(seedA: number, seedB: number): number | null {
  const key = `${seedA}v${seedB}`;
  return SEED_MATCHUP_PRIORS[key] ?? null;
}

/**
 * Blend MMER model probability with historical seed priors.
 * Uses 65% model weight, 35% historical prior.
 * Falls back to pure model if no seed prior exists.
 */
export function calibrateWithSeedPrior(
  rawProb: number,
  seedA?: number,
  seedB?: number,
  modelWeight = 0.65,
): number {
  if (seedA == null || seedB == null) return rawProb;
  const prior = getSeedPrior(seedA, seedB);
  if (prior === null) return rawProb;
  
  // Weighted blend: model + historical base rate
  const blended = modelWeight * rawProb + (1 - modelWeight) * prior;
  // Clamp to 1-99%
  return Math.max(0.01, Math.min(0.99, blended));
}


// ── Live Win Probability ───────────────────────────────────────────────────

/**
 * Live projected final score based on current pace + MMER pregame projection.
 * Blends pregame expected score with pace-adjusted projection from live data.
 */
export function liveProjectedScore(
  pregameScoreA: number, pregameScoreB: number,
  currentScoreA: number, currentScoreB: number,
  clock: string, period: number, status: string
): { projA: number; projB: number } | null {
  if (status !== 'live' && status !== 'halftime') return null;

  // Calculate time elapsed and remaining
  const clockParts = clock.split(':').map(Number);
  const clockSec = (clockParts[0] || 0) * 60 + (clockParts[1] || 0);
  const totalGameSec = 2400; // 40 min game

  let elapsedSec: number;
  if (status === 'halftime') {
    elapsedSec = 1200;
  } else if (period === 1) {
    elapsedSec = 1200 - clockSec;
  } else {
    elapsedSec = 1200 + (1200 - clockSec);
  }

  const fraction = Math.max(0.01, Math.min(elapsedSec / totalGameSec, 0.99));

  // Pace-based projection: extrapolate current scoring rate to full game
  const paceA = currentScoreA / fraction;
  const paceB = currentScoreB / fraction;

  // Blend: early game → mostly pregame, late game → mostly pace
  const projA = Math.round(pregameScoreA * (1 - fraction) + paceA * fraction);
  const projB = Math.round(pregameScoreB * (1 - fraction) + paceB * fraction);

  return { projA, projB };
}

export function liveWinProbability(
  pregameWinProbA: number,
  scoreA: number, scoreB: number,
  clock: string, period: number, status: string
): { winProbA: number; winProbB: number; momentum: string } {
  if (status === 'pre') return { winProbA: pregameWinProbA, winProbB: 1 - pregameWinProbA, momentum: '—' };
  if (status === 'final') {
    const w = scoreA > scoreB ? 1 : scoreA < scoreB ? 0 : 0.5;
    return { winProbA: w, winProbB: 1 - w, momentum: '—' };
  }

  // Parse clock to seconds remaining
  const parts = clock.replace(/\s/g, '').split(':');
  const mins = parseInt(parts[0] || '0', 10);
  const secs = parseInt(parts[1] || '0', 10);
  const clockSecs = mins * 60 + secs;
  
  // Total game = 40 min (2400 sec). Period 1 = first 20 min.
  const totalSecs = 2400;
  const secsRemaining = period === 1 ? clockSecs + 1200 : clockSecs;
  const timeElapsedPct = Math.max(0, Math.min(1, 1 - secsRemaining / totalSecs));

  // Score differential
  const scoreDiff = scoreA - scoreB;
  
  // Pregame edge in logit space
  const pregameLogit = Math.log(pregameWinProbA / (1 - pregameWinProbA));
  
  // Score-based logit: ~0.15 per point of lead per game-fraction elapsed
  // This is a simplified model; real models use possession-adjusted data
  const scoreLogit = scoreDiff * 0.15;
  
  // Blend: pregame weight decreases as game progresses
  const pregameWeight = 1 - timeElapsedPct;
  const scoreWeight = timeElapsedPct;
  
  // Combined logit — score matters more as time runs out
  // Late-game amplification: multiply score impact when <5 min remain
  const lateGameBoost = secsRemaining < 300 ? 1.5 : 1.0;
  const combinedLogit = pregameLogit * pregameWeight + scoreLogit * scoreWeight * lateGameBoost;
  
  // Convert back to probability
  const rawProb = 1 / (1 + Math.exp(-combinedLogit));
  // Clamp to 1-99
  const winProbA = Math.max(0.01, Math.min(0.99, rawProb));
  
  // Momentum indicator
  const momentum = scoreDiff > 0 ? '↑' : scoreDiff < 0 ? '↓' : '→';
  
  return { winProbA, winProbB: 1 - winProbA, momentum };
}

// ── Injury Data ────────────────────────────────────────────────────────────
export interface PlayerInjury {
  teamId: string;       // matches teams.ts id (e.g., 'team-duke')
  espnId?: string;
  playerName: string;
  status: 'OUT' | 'GTD' | 'QUESTIONABLE' | 'PROBABLE';
  detail?: string;      // e.g., "knee", "ankle"
  impact?: string;      // e.g., "starter", "key rotation"
}

export interface InjuryData {
  lastUpdated: string;
  injuries: PlayerInjury[];
}

let _injuryData: InjuryData | null = null;

export async function loadInjuryData(): Promise<void> {
  try {
    const res = await fetch('/injuries.json');
    _injuryData = await res.json();
  } catch { _injuryData = { lastUpdated: '', injuries: [] }; }
}

export function getTeamInjuries(teamId: string): PlayerInjury[] {
  return _injuryData?.injuries?.filter(i => i.teamId === teamId) || [];
}

export function hasInjuries(teamId: string): boolean {
  return getTeamInjuries(teamId).length > 0;
}
